import { useFocusEffect } from '@react-navigation/native';
import { useCallback, type RefObject } from 'react';
import { Platform, type TextInput } from 'react-native';

function isEditableTarget(target: EventTarget | null): boolean {
  if (target == null || typeof target !== 'object') return false;
  const el = target as { tagName?: string; isContentEditable?: boolean };
  const tag = el.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
}

type Options = {
  /** Registrar atajos F2 y `/` mientras la pantalla está enfocada. */
  shortcutsEnabled?: boolean;
};

/**
 * Web operativo: F2 o `/` enfocan el buscador mientras la pantalla tiene foco.
 * Nunca enfoca al entrar a la pantalla (ni al volver de otra tab).
 */
export function useOperationalWebSearchFocus(
  inputRef: RefObject<TextInput | null>,
  { shortcutsEnabled = true }: Options = {}
) {
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web' || typeof window === 'undefined' || !shortcutsEnabled) {
        return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'F2') {
          event.preventDefault();
          inputRef.current?.focus();
          return;
        }
        if (
          event.key === '/' &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          !isEditableTarget(event.target)
        ) {
          event.preventDefault();
          inputRef.current?.focus();
        }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    }, [inputRef, shortcutsEnabled])
  );
}
